import React, { useReducer, useEffect, useState } from 'react';
import './chat.css';

import { chatbotReducer } from 'context/chatbot/reducer';
import { listChatbotOptions, runChatbotAction } from 'context/chatbot/actions';
import { formatDynamicMessage } from 'utils/formatServiceMessage';
import { buildDynamicOptionName } from 'utils/icon_mapper';

const FloatingChatWindow = ({ onClose }) => {
  const [state, dispatch] = useReducer(chatbotReducer, {
    options: [],
    loadingChatbotOptions: false,
  });

  const { options, loadingChatbotOptions } = state;

  const [inputEnabled, setInputEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [flowInteractiveChild, setFlowInteractiveChild] = useState(null);
  const [autoListChild, setAutoListChild] = useState(null);
  const [showRestart, setShowRestart] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState(null);
  const [minimized, setMinimized] = useState(false);
  const [optionsDisabled, setOptionsDisabled] = useState(false);
  const [lastActionKey, setLastActionKey] = useState(null);
  const [lastFlowType, setLastFlowType] = useState(null);
  const [optionsHistory, setOptionsHistory] = useState([]);

  useEffect(() => {
    listChatbotOptions(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (options.length > 0) {
      setCurrentOptions(options);
      setConversation((prev) => [
        ...prev,
        {
          from: 'bot',
          text: '👋 ¡Hola! Soy PiBoxi, Como van nuestros envíos hoy? Dime ¿en qué puedo ayudar?',
        },
      ]);
    }
  }, [options]);

  useEffect(() => {
    const container = document.querySelector('.chat-conversation');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [conversation.length]);

  const handleOptionClick = async (option) => {
    setConversation((prev) => [...prev, { from: 'user', text: option.name }]);
    setSelectedOption(option);

    const pushToHistory = () => {
      setOptionsHistory((prev) => {
        const last = prev[prev.length - 1];
        if (JSON.stringify(last) === JSON.stringify(currentOptions)) return prev;
        return [...prev, currentOptions];
      });
    };

    if (option.message && (option.message === 'Click' || option.message?.es === 'Click')) {
      restartChat();
      return;
    }

    if (option.flow_type === 'auto_list_interactive') {
      const result = await runChatbotAction(dispatch, option.action_key, option.id);

      if (result?.error) {
        const newFails = failedAttempts + 1;
        setFailedAttempts(newFails);
        setConversation((prev) => [...prev, { from: 'bot', text: result.message }]);

        if (newFails >= 3) {
          setConversation((prev) => [...prev, {
            from: 'bot',
            text: 'Parece que hubo varios errores. ¿Deseas intentar otra opción?'
          }]);
          setInputEnabled(false);
          setSelectedOption(null);
          setAutoListChild(null);
          setFlowInteractiveChild(null);
          setActiveBookingId(null);
          setCurrentOptions(options);
          setOptionsHistory([]);
          setFailedAttempts(0);
        }
        return;
      }

      const child_flow_type = option.childs?.[0]?.flow_type;
      const child_action_key = option.childs?.[0]?.action_key;

      if (Array.isArray(result)) {
        const dynamicOptions = result.map((item) => ({
          id: item.id,
          name: item.name || buildDynamicOptionName(item),
          message: item.message || '',
          flow_type: item.flow_type || child_flow_type,
          action_key: item.action_key || child_action_key
        }));

        setConversation((prev) => [...prev, { from: 'bot', text: 'Selecciona una opción:' }]);
        pushToHistory();
        setCurrentOptions(dynamicOptions);
        setInputEnabled(false);
        setSelectedOption(null);
        setAutoListChild(option.childs?.[0] || null);
        return;
      }
    }

    if (option.flow_type === 'flow') {
      if (option.childs && option.childs.length > 0) {
        pushToHistory();
        setCurrentOptions(option.childs);
        setInputEnabled(false);
      } else if (option.action_key) {
        const result = await runChatbotAction(dispatch, option.action_key, option.id);

        if (result?.error) {
          const newFails = failedAttempts + 1;
          setFailedAttempts(newFails);
          setConversation((prev) => [...prev, { from: 'bot', text: result.message }]);

          if (newFails >= 3) {
            setConversation((prev) => [...prev, {
              from: 'bot',
              text: 'Parece que hubo varios errores. ¿Deseas intentar otra opción?'
            }]);
            setInputEnabled(false);
            setSelectedOption(null);
            setActiveBookingId(null);
            setCurrentOptions(options);
            setOptionsHistory([]);
            setFailedAttempts(0);
          }
          return;
        }

        const formatted = typeof result === 'string' ? result : formatDynamicMessage(result || {});
        setConversation((prev) => [...prev, { from: 'bot', text: formatted }]);
        setCurrentOptions([]);
        setInputEnabled(false);
        setShowRestart(true);
      } else if (option.message) {
        setConversation((prev) => [...prev, { from: 'bot', text: option.message }]);
        setCurrentOptions([]);
        setInputEnabled(false);
        setShowRestart(true);
      } else {
        setCurrentOptions([]);
        setInputEnabled(false);
        setShowRestart(true);
      }
    } else if (option.flow_type === 'interactive') {
      if (option.message) {
        setConversation((prev) => [...prev, { from: 'bot', text: option.message }]);
      }
      setCurrentOptions([]);
      setInputEnabled(true);
    } else if (option.flow_type === 'flow_interactive') {
      if (option.message) {
        setConversation((prev) => [...prev, { from: 'bot', text: option.message }]);
      }
      pushToHistory();
      setCurrentOptions([]);
      setInputEnabled(true);
      setFlowInteractiveChild(option.childs?.[0] || null);

      setLastActionKey(option.action_key || null);
      setLastFlowType(option.flow_type);
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() === '') return;

    setConversation((prev) => [...prev, { from: 'user', text: message }]);

    const action = selectedOption?.action_key || lastActionKey;
    const flowType = selectedOption?.flow_type || lastFlowType;
    const isFlowInteractive = flowType === 'flow_interactive';

    const endpoint = (action?.endpoint || '').replace(':id', activeBookingId || message);
    const updatedAction = { ...action, endpoint };

    const result = await runChatbotAction(dispatch, updatedAction, message);

    if (result?.error) {
      const newFails = failedAttempts + 1;
      setFailedAttempts(newFails);
      setConversation((prev) => [...prev, { from: 'bot', text: result.message }]);

      if (newFails >= 3) {
        setConversation((prev) => [
          ...prev,
          { from: 'bot', text: 'Parece que hubo varios errores. ¿Deseas intentar otra opción?' }
        ]);
        setInputEnabled(false);
        setSelectedOption(null);
        setAutoListChild(null);
        setFlowInteractiveChild(null);
        setActiveBookingId(null);
        setCurrentOptions(options);
        setFailedAttempts(0);
      }

      setMessage('');
      return;
    }

    if (Array.isArray(result)) {
      if (result.length === 0) {
        setConversation((prev) => [...prev, {
          from: 'bot',
          text: 'No se encontraron resultados. Intenta con otra fecha o parámetro.'
        }]);
        setInputEnabled(isFlowInteractive); // 🟢 mantiene input activo
        return;
      }

      const dynamicOptions = result.map((item) => ({
        id: item.id,
        name: item.name || buildDynamicOptionName(item),
        message: item.message || '',
        flow_type: item.flow_type || 'interactive',
        action_key: item.action_key || null
      }));

      setConversation((prev) => [...prev, { from: 'bot', text: 'Selecciona uno de los resultados encontrados:' }]);
      setCurrentOptions(dynamicOptions);
      setInputEnabled(isFlowInteractive); // 🟢 mantiene input activo si aplica
      setSelectedOption(null);
      setMessage('');
      return;
    }

    if (result) {
      const formatted = typeof result === 'string' ? result : formatDynamicMessage(result || {});
      setConversation((prev) => [...prev, { from: 'bot', text: formatted }]);

      if (typeof result === 'object' && result.id) {
        setActiveBookingId(result.id);
      }

      const child = selectedOption?.childs?.[0];
      if (isFlowInteractive && child) {
        if (child.flow_type === 'interactive') {
          if (child.message) {
            setConversation((prev) => [...prev, { from: 'bot', text: child.message }]);
          }
          setInputEnabled(true);
          setSelectedOption(child);
          setMessage('');
          return;
        } else if (child.flow_type === 'flow' && child.childs?.length > 0) {
          if (child.message) {
            setConversation((prev) => [...prev, { from: 'bot', text: child.message }]);
          }
          setCurrentOptions(child.childs);
          setInputEnabled(false);
          setSelectedOption(null);
          setMessage('');
          return;
        }
      }

      if (selectedOption?.childs?.length > 0) {
        setCurrentOptions(selectedOption.childs);
        setInputEnabled(false);
        setSelectedOption(null);
        setMessage('');
        return;
      }

      setInputEnabled(false);
      setFailedAttempts(0);
      setShowRestart(true);
      setMessage('');
    }
  };
  
  const handleAutomaticAction = async (userText, option) => {
    setConversation((prev) => [...prev, { from: 'user', text: userText }]);

    const endpoint = (option.action_key?.endpoint || '').replace(':id', activeBookingId || option.id);
    const updatedAction = { ...option.action_key, endpoint };

    const result = await runChatbotAction(dispatch, updatedAction, userText);

    const child_flow_type = option.childs?.[0]?.flow_type;
    const child_action_key = option.childs?.[0]?.action_key;

    if (Array.isArray(result)) {
      if (result.length === 0) {
        setConversation((prev) => [...prev, { from: 'bot', text: 'No se encontraron resultados. Intenta con otra opción.' }]);
        setInputEnabled(false);
        setShowRestart(true);
        return;
      }

      const dynamicOptions = result.map((item) => ({
        id: item.id,
        name: item.name || buildDynamicOptionName(item),
        message: item.message || '',
        flow_type: item.flow_type || child_flow_type,
        action_key: item.action_key || child_action_key
      }));

      setConversation((prev) => [...prev, { from: 'bot', text: 'Selecciona uno de los resultados encontrados:' }]);
      setCurrentOptions(dynamicOptions);
      setInputEnabled(false);
      setSelectedOption(null);

      if (option.flow_type === 'auto_list_interactive') {
        setAutoListChild(option.childs?.[0] || null);
        setFlowInteractiveChild(null);
      } else if (option.flow_type === 'flow_interactive') {
        setFlowInteractiveChild(option.childs?.[0] || null);
        setAutoListChild(null);
      }

      setShowRestart(false);
      return;
    }

    if (result) {
      if (result?.error) {
        setConversation((prev) => [...prev, { from: 'bot', text: result.message }]);
        setInputEnabled(false);
        setCurrentOptions([]);
        setSelectedOption(null);
        setFlowInteractiveChild(null);
        setAutoListChild(null);
        setShowRestart(true);
        return;
      }

      const formatted = typeof result === 'string' ? result : formatDynamicMessage(result || {});
      setConversation((prev) => [...prev, { from: 'bot', text: formatted }]);

      if (typeof result === 'object' && result.id) {
        setActiveBookingId(result.id);
      }

      if (typeof result === 'object' && option?.childs?.length > 0) {
        setFlowInteractiveChild(option.childs[0]);
        setCurrentOptions(option.childs);
        setInputEnabled(false);
        setSelectedOption(null);
        return;
      }

      setInputEnabled(false);
      setCurrentOptions([]);
      setSelectedOption(null);
      setFlowInteractiveChild(null);
      setAutoListChild(null);
      setShowRestart(true);
      return;
    }

    setConversation((prev) => [...prev, { from: 'bot', text: 'No pudimos procesar tu selección. Intenta otra opción.' }]);
    setInputEnabled(false);
    setCurrentOptions([]);
    setSelectedOption(null);
    setFlowInteractiveChild(null);
    setAutoListChild(null);
    setShowRestart(true);
  };

  const restartChat = () => {
    setConversation([]);
    setCurrentOptions([]);
    setSelectedOption(null);
    setInputEnabled(false);
    setFlowInteractiveChild(null);
    setActiveBookingId(null);
    setAutoListChild(null);
    setShowRestart(false);
    listChatbotOptions(dispatch);
    setOptionsHistory([]);
  };

const goBack = () => {

  setOptionsHistory((prevHistory) => {
    if (prevHistory.length <= 1) return prevHistory;

    const newHistory = [...prevHistory];
    newHistory.pop();
    const previousOptions = newHistory[newHistory.length - 1];

    setCurrentOptions(previousOptions);

    setConversation((prev) => {
      const updated = [...prev];
      if (updated.length >= 2) {
        updated.pop();
        updated.pop();
      }
      return updated;
    });

    return newHistory;
  });

  setMessage('');
};

  return (
    <div className="floating-chat-window">
      <div className="chat-header">
        <span>Asistencia en línea</span>
        <div>
          <button className="close-button" onClick={() => setMinimized(!minimized)}>
            <span role="img" aria-label="minimizar">➖</span>
          </button>
          <button className="close-button" onClick={onClose}>
            <span role="img" aria-label="cerrar">✖️</span>
          </button>
        </div>
      </div>

      {!minimized && (
        <>
          <div className="chat-content">
            {loadingChatbotOptions ? (
              <div className="chat-message">Cargando opciones...</div>
            ) : (
              <>
                <div className="chat-conversation">
                  {conversation.map((msg, index) => (
                    <div
                      key={index}
                      className={`chat-bubble ${msg.from === 'bot' ? 'bot-message' : 'user-message'}`}
                      dangerouslySetInnerHTML={{ __html: (msg.text || '').replace(/\n/g, '<br />') }}
                    />
                  ))}
                </div>

                {currentOptions.length > 0 && (
                  <div className="chat-options">
                    {currentOptions.map((option) => (
                      <button
                        key={option.id || option._id || Math.random().toString(36).substr(2, 9)}
                        className="chat-option-button"
                        disabled={optionsDisabled}
                        onClick={async () => {
                          setOptionsDisabled(true);
                          setConversation((prev) => [...prev, { from: 'bot', text: 'Procesando...' }]);

                          const source = autoListChild || flowInteractiveChild;
                          try {
                            if (source?.action_key?.endpoint) {
                              const childWithParams = {
                                ...source,
                                id: option.id,
                                name: option.name,
                                action_key: {
                                  ...source.action_key,
                                  endpoint: (source.action_key.endpoint || '').replace(':id', option.id)
                                }
                              };
                              if (option.message === 'Click') {
                                restartChat();
                                return;
                              } else {
                                await handleAutomaticAction(option.name, childWithParams);
                              }
                            } else {
                              await handleOptionClick(option);
                            }
                          } finally {
                            setOptionsDisabled(false);
                          }
                        }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: (option.name || '').replace(/\n/g, '<br />') }} />
                      </button>
                    ))}

                    {optionsHistory.length > 1 && (
                      <button className="chat-option-button back-button" onClick={goBack}>
                        Regresar
                      </button>
                    )}
                  </div>
                )}

                {showRestart && (
                  <div className="chat-restart">
                    <button className="restart-button" onClick={restartChat}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#7a1fa2" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 1 1 3.89 6.62M4.5 12V7.5M4.5 12H9" />
                      </svg>
                      Menu Principal
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="chat-input">
            <input
              type="text"
              placeholder="Escribe tu respuesta..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputEnabled) {
                  handleSendMessage();
                }
              }}
              disabled={!inputEnabled}
              className={inputEnabled ? '' : 'input-disabled'}
            />
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={!inputEnabled}
            >
              ➔
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FloatingChatWindow;
